import { useEffect, useMemo, useState, useRef } from "react";
import { Check, ChevronDown, User as UserIcon, X } from "lucide-react";
import api from "@/lib/api";

/** Shared cache so many rows don't refetch */
let _directoryCache = null;
let _directoryPromise = null;
async function loadDirectory() {
  if (_directoryCache) return _directoryCache;
  if (!_directoryPromise) {
    _directoryPromise = api.get("/users/directory").then((r) => {
      _directoryCache = r.data || [];
      return _directoryCache;
    }).catch(() => { _directoryPromise = null; return []; });
  }
  return _directoryPromise;
}
export function useDirectory() {
  const [dir, setDir] = useState(_directoryCache || []);
  useEffect(() => { if (!_directoryCache) loadDirectory().then(setDir); }, []);
  return dir;
}

function normalize(s) {
  return (s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().trim();
}

/**
 * User picker input. Only users from /users/directory can be selected.
 * value: { user_id, nombre }
 * onChange: (value) => void
 */
export default function UserPicker({ value, onChange, placeholder, testId, className = "", invalid }) {
  const users = useDirectory();
  const [q, setQ] = useState(value?.nombre || "");
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => { setQ(value?.nombre || ""); }, [value?.nombre, value?.user_id]);

  useEffect(() => {
    const onClick = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    const nq = normalize(q);
    if (!nq) return users.slice(0, 20);
    return users
      .map((u) => ({ u, s: normalize(u.name).includes(nq) || normalize(u.email).includes(nq) }))
      .filter((x) => x.s)
      .map((x) => x.u)
      .slice(0, 20);
  }, [q, users]);

  const pick = (u) => {
    onChange({ user_id: u.id, nombre: u.name });
    setQ(u.name);
    setOpen(false);
  };

  const clear = () => { onChange({ user_id: null, nombre: "" }); setQ(""); setOpen(false); };

  const hasValidUser = !!value?.user_id;

  return (
    <div ref={wrap} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          data-testid={testId}
          className={`brutal-input w-full pr-16 ${invalid && !hasValidUser ? "border-rose-500" : ""}`}
          placeholder={placeholder || "Escribe el nombre…"}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            // Invalidate user_id if the user is editing text — must reselect
            if (value?.user_id && e.target.value !== value.nombre) {
              onChange({ user_id: null, nombre: e.target.value });
            }
          }}
          onFocus={() => setOpen(true)}
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {hasValidUser && <Check size={16} className="text-emerald-600" />}
          {q && (
            <button type="button" onClick={clear} className="w-6 h-6 rounded-full hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" tabIndex={-1}>
              <X size={12} />
            </button>
          )}
          <button type="button" onClick={() => setOpen((o) => !o)} className="w-6 h-6 rounded-full hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" tabIndex={-1}>
            <ChevronDown size={14} />
          </button>
        </div>
      </div>
      {open && (
        <div
          className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {filtered.length === 0 ? (
            <p className="p-3 text-xs text-slate-500 text-center">Sin resultados. Debes elegir un usuario existente.</p>
          ) : filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => pick(u)}
              data-testid={testId ? `${testId}-opt-${u.id}` : undefined}
              className="w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="w-7 h-7 rounded-full bg-[#F5A623] text-white text-xs font-bold flex items-center justify-center">
                {(u.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[13px] truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{u.email} · {u.empresa || ""}</div>
              </div>
              <UserIcon size={12} className="text-slate-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Best-effort fuzzy match by name/email — used by voice parser mapping. */
export function findUserByName(users, spokenName) {
  const target = normalize(spokenName);
  if (!target) return null;
  // 1. Exact case-insensitive match on name
  let hit = users.find((u) => normalize(u.name) === target);
  if (hit) return hit;
  // 2. Prefix or contains
  hit = users.find((u) => normalize(u.name).startsWith(target)) ||
        users.find((u) => normalize(u.name).includes(target)) ||
        users.find((u) => normalize(u.email).includes(target));
  return hit || null;
}
